// components/ActionChips.tsx — ported from docs/superpowers/design-2026-04-21/messages.jsx

import type { JSX, ReactNode } from 'react';
import { useTokens } from '../state/theme';

export type ActionChipsAction = {
  kind?: 'primary';
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  /** Optional explicit disabled state for stubs/unimplemented actions. */
  disabled?: boolean;
  /** Tooltip shown on hover, useful to explain why a chip is disabled. */
  title?: string;
};

export interface ActionChipsProps {
  actions: ActionChipsAction[];
  /**
   * 'wrap' (default): chips wrap onto multiple lines — fine for the empty
   * state where there is room. 'row' (DP1, founder device pass 2026-07-13):
   * ONE horizontally scrollable line — used for post-answer follow-up
   * suggestions, which used to stack vertically and eat half the phone
   * screen. The row hides its scrollbar and lets the thumb swipe through.
   */
  layout?: 'wrap' | 'row';
}

export function ActionChips({ actions, layout = 'wrap' }: ActionChipsProps): JSX.Element | null {
  const tokens = useTokens();
  if (!actions || !actions.length) return null;
  // V28 S1 — follow-up / quick-action chips match the prototype `.chip`
  // (kit.css §5 / prof-chat.html): a gradient-tint pill on `--brand-grad-soft`,
  // brand-ink text, hairline violet edge, 36px tap height, weight 600. The
  // optional `primary` variant stays the brand-gradient block CTA (white text +
  // arrow) used for the empty-state "Riprendi da lì" and the retry chip.
  const tintBg = tokens.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(124,90,255,0.18), rgba(255,90,216,0.16))'
    : 'linear-gradient(90deg, #f0e9ff, #fbeefa)'; // prototype --brand-grad-soft
  const tintBorder = tokens.mode === 'dark' ? 'rgba(124,90,255,0.30)' : 'rgba(105, 67, 255, 0.14)';
  const row = layout === 'row';
  return (
    <div
      className={row ? 'asc-chip-row' : undefined}
      style={row
        ? {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 8,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            // breathing room so the pill shadow/edge isn't clipped
            padding: '2px 2px 4px',
            scrollbarWidth: 'none',
          }
        : { marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {actions.map((a, i) => {
        const primary = a.kind === 'primary';
        const disabled = a.disabled || !a.onClick;
        return (
          <button
            key={i}
            onClick={disabled ? undefined : a.onClick}
            disabled={disabled}
            title={a.title}
            style={{
              background: primary ? tokens.brandGradient : tintBg,
              color: primary ? '#fff' : tokens.accentInk,
              border: primary ? '1px solid transparent' : `1px solid ${tintBorder}`,
              borderRadius: tokens.radius.pill,
              minHeight: 36, padding: '0 12px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: tokens.fonts.body, fontSize: 13,
              fontWeight: primary ? 600 : 600,
              letterSpacing: -0.05,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: primary && !disabled
                ? (tokens.mode === 'dark'
                    ? '0 6px 16px rgba(124,90,255,0.30)'
                    : '0 6px 16px rgba(105, 67, 255, 0.24)')
                : 'none',
              opacity: disabled ? 0.45 : 1,
              transition: 'filter .16s, transform .12s',
            }}
            onMouseOver={(e) => { if (!disabled) e.currentTarget.style.filter = primary ? 'brightness(1.04)' : 'brightness(0.98)'; }}
            onMouseOut={(e) => { e.currentTarget.style.filter = 'none'; }}
          >
            {a.icon}
            {a.label}
            {primary && !disabled && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M3 6h6M7 3l3 3-3 3"/>
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}
